<template>
    <div class="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6 transition-all duration-200 ease-in-out"
        :class="[state.saved ? 'h-18' : 'h-auto']">
        <div class="md:grid md:grid-cols-3 md:gap-6" v-if="!state.saved">
            <div class="md:col-span-1">
                <h3 class="text-lg font-medium leading-6 text-gray-900">Customer</h3>
                <p class="mt-1 text-sm text-gray-500">Provide the customer's information, if the customer already has an account, it'll be automatically matched.</p>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
                <form class="space-y-6" method="POST" @submit.prevent>
                    <div class="grid grid-cols-3 gap-6">
                        <div class="col-span-6 sm:col-span-4">
                            <label for="email-address"
                                   class="block text-sm font-medium text-gray-700">Email address</label>

                            <div class="flex mb-4">
                                <input type="text"
                                       v-model="state.email"
                                       name="email-address"
                                       id="email-address"
                                       autocomplete="email"
                                       class="flex-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-none rounded-l-md"/>

                                <button @click="retrieveCustomer(v$)" type="button" class="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500">
                                    <CheckCircleIcon class="h-5 w-5 text-gray-400" aria-hidden="true" v-if="!loading" />
                                    <ArrowPathIcon class="h-5 w-5 text-gray-400 animate-spin" aria-hidden="true" v-if="loading" />
                                    <span>Check</span>
                                </button>
                            </div>
                            <p
                                v-for="error of v$.email.$errors"
                                :key="error.$uid"
                                class="text-red-700 text-sm -mt-2 mb-2"
                            >
                                {{ error.$message }}
                            </p>

                            <div class="border-2 border-dashed border-gray-200 p-4">
                                <div v-if="newCustomer === false" class="text-sm text-gray-600">
                                    <p class="font-normal text-xl mb-2">Existing customer found</p>
                                    <p class="font-bold text-md">{{ state.name }}&nbsp;<span class="text-gray-400 text-xs font-normal italic">( #{{ state.id }} )</span></p>
                                    <p class="font-normal text-md">{{ state.email }}</p>
                                    <p class="font-normal">
                                        <span class="font-bold">Created on:</span> {{ formatDateShort(state.created) }}<br />
                                        <span class="font-bold">Last login:</span> {{ formatDateRelative(state.login) }}
                                    </p>
                                    <div class="p-2 mt-2 rounded-sm bg-green-100 border border-green-500 text-green-500"
                                         v-if="state.subscription?.package?.id">
                                        <p>
                                            Customer is currently subscribed to:<br />
                                            <strong>{{ state.subscription.package.name }}</strong><br />
                                            Term: {{ subscriptionTerm }}
                                        </p>
                                    </div>
                                    <div class="flex justify-between mt-4">
                                        <button type="submit" @click="proceed(v$)" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                            <ChevronDownIcon class="h-5 w-5 mr-2" />
                                            Proceed
                                        </button>
                                        <button type="button" @click="passwordReset"
                                                :class="[passwordHasBeenReset ? 'border-green-400 text-green-800 bg-green-200' : 'border-gray-300 text-gray-700 bg-white']"
                                                :disabled="passwordHasBeenReset"
                                                class="inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                            <EnvelopeIcon v-if="!passwordHasBeenReset" class="h-5 w-5 mr-2" />
                                            <CheckCircleIcon v-if="passwordHasBeenReset" class="h-5 w-5 mr-2" />
                                            Reset Password
                                        </button>
                                    </div>
                                </div>

                                <div v-if="newCustomer === true" class="text-sm text-gray-600">
                                    <div>
                                        <label for="name" class="block text-sm font-medium text-gray-700">Name</label>
                                        <div class="mt-1 mb-2 relative flex items-stretch flex-grow">
                                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <UsersIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                                            </div>
                                            <input v-model="v$.name.$model" type="text" name="name" id="name" class="pl-10 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Jane Doe" />
                                        </div>
                                        <p
                                            v-for="error of v$.name.$errors"
                                            :key="error.$uid"
                                            class="text-red-700 text-sm mb-2"
                                        >
                                            {{ error.$message }}
                                        </p>
                                    </div>

                                    <div>
                                        <label for="telephone" class="block text-sm font-medium text-gray-700">Telephone</label>
                                        <div class="mt-1 mb-2 relative flex items-stretch flex-grow">
                                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <PhoneIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                                            </div>
                                            <input v-model="v$.telephone.$model" type="text" name="telephone" id="telephone" class="pl-10 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="01234 567890" />
                                        </div>
                                        <p
                                            v-for="error of v$.telephone.$errors"
                                            :key="error.$uid"
                                            class="text-red-700 text-sm mb-2"
                                        >
                                            {{ error.$message }}
                                        </p>
                                    </div>

                                    <div class="flex justify-center mt-4">
                                        <button type="submit" @click="proceed(v$)" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                            <ArrowPathIcon class="h-5 w-5 mr-2 animate-spin" v-if="customerSaving" />
                                            <ChevronDownIcon class="h-5 w-5 mr-2" v-if="!customerSaving" />
                                            Create Customer
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <div class="md:grid md:grid-cols-3 md:gap-6" v-if="state.saved">
            <div class="md:col-span-1 flex">
                <CheckCircleIcon v-if="state" class="h-6 w-6 text-green-600 mr-4" />
                <h3 class="text-lg font-medium leading-6 text-gray-900 flex-1">Customer</h3>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
                <p class="font-bold text-md">{{ state.name }}&nbsp;<span class="text-gray-400 text-xs font-normal italic">( #{{ state.id }} )</span></p>
                <p class="font-normal text-md">{{ state.email }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import {computed, inject, onMounted, ref, toRef} from "vue"
import {CheckCircleIcon, ArrowPathIcon, ChevronDownIcon, EnvelopeIcon} from "@heroicons/vue/24/outline"
import {email, required, requiredIf } from "@vuelidate/validators"
import useVuelidate from "@vuelidate/core"
import {PhoneIcon, UsersIcon} from "@heroicons/vue/24/solid"
import {useRoute} from "vue-router"

export default {
    name: "Customer",
    components: {
        ArrowPathIcon,
        CheckCircleIcon,
        ChevronDownIcon,
        EnvelopeIcon,
        UsersIcon,
        PhoneIcon,
    },
    props: {
        customer: {
            type: Object,
            required: true,
        }
    },
    setup(props) {
        const vueRoute = useRoute()
        const alert = inject('alert')
        const passwordHasBeenReset = ref(false)
        const newCustomer = ref(null)
        const loading = ref(false)
        const customerSaving = ref(false)

        const state = toRef(props, 'customer')

        const rules = computed(() => ({
            email: { required, email },
            name: { requiredIfNew: requiredIf(!Object.keys(state.value).includes('id')) },
            telephone: { requiredIfNew: requiredIf(!Object.keys(state.value).includes('id')) }
        }))

        const v$ = useVuelidate(rules, state, { $lazy: true })

        const retrieveCustomer = async (v$) => {
            v$.email.$touch()

            loading.value = true

            if (v$.email.$invalid) {
                loading.value = false
                return;
            }

            await axios.get(route('users.show.email', { email: state.value.email }))
                .then((response) => {
                    loading.value = false
                    if (!response.data.user) {
                        newCustomer.value = true
                    } else {
                        newCustomer.value = false

                        state.value.id = response.data.user?.id
                        state.value.account_id = response.data.user?.account_id
                        state.value.email = response.data.user?.email
                        state.value.name = response.data.user?.profile.name
                        state.value.telephone = response.data.user?.profile.telephone
                        state.value.created = response.data.user?.created
                        state.value.login = response.data.user?.last_login
                        state.value.customer_id = response.data.user?.profile.stripe_customer
                        state.value.subscription = {
                            data: response.data.subscription?.data,
                            package: response.data.subscription?.package,
                            interval: response.data.subscription?.package?.interval,
                            cost: response.data.subscription?.cost,
                        }
                    }
                })
        }

        const prefillCustomer = async () => {
            if (vueRoute.params.userId) {
                await axios.get(route('users.show', vueRoute.params.userId))
                    .then((response) => {
                        state.value.email = response.data.user.email
                    });
            }
        }

        const proceed = async (v$) => {
            const isValid = await v$.$validate()

            if (!isValid) {
                return;
            }

            if (newCustomer.value) {
                customerSaving.value = true
                await axios.post(route('users.create.customer'), state.value)
                    .then((response) => {
                        state.value.id =  response.data.id
                        state.value.account_id = response.data.account_id
                        state.value.email = response.data.email
                        state.value.name = response.data.profile?.name
                        state.value.telephone = response.data.profile?.telephone
                        state.value.created = response.data.created
                        state.value.login = response.data.last_login
                        state.value.customer_id = response.data.profile?.stripe_customer

                        customerSaving.value = false
                    })
            }

            state.value.saved = true
        }

        const passwordReset = () => {
            alert.fire({
                title: 'Confirm Reset Password',
                text: 'Confirm you wish to reset the user\'s account password. This will send an email to the user containing a link to continue the process',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('users.reset-password', {user: state.value.id}))
                            .then((response) => {
                                passwordHasBeenReset.value = true
                            })
                    }
                })
        }

        const subscriptionTerm = computed(() => {
            if (state.value.subscription?.package.interval === 12) {
                return 'Annual'
            } else if (state.value.subscription?.package.interval === 6) {
                return '6 Monthly'
            } else if (state.value.subscription?.package.interval === 3) {
                return 'Quarterly'
            } else {
                return 'Monthly'
            }
        })

        onMounted(() => {
            prefillCustomer()
        })

        return {
            v$,
            state,
            loading,
            newCustomer,
            retrieveCustomer,
            passwordReset,
            passwordHasBeenReset,
            proceed,
            customerSaving,
            subscriptionTerm,
        }
    }
}
</script>

<style scoped>

</style>
